import { z } from 'zod';
import {
  DISMISSAL_MEMORIES,
  GUIDANCE_COMPONENTS,
  GUIDANCE_STATES,
  anchorSpecSchema,
  guidanceContentSchema,
  triggerRuleSchema,
} from '../configuration/guidance.schema.js';

/**
 * There is deliberately NO create DTO. Guidances are only ever produced by the
 * engine from a recommendation (doc technique §7.2, invariant 3).
 */
export const updateGuidanceDto = z
  .object({
    component: z.enum(GUIDANCE_COMPONENTS).optional(),
    trigger: triggerRuleSchema.optional(),
    dismissalMemory: z.enum(DISMISSAL_MEMORIES).optional(),
    reinforced: z.boolean().optional(),
    contents: z.record(guidanceContentSchema).optional(),
    stepId: z.string().uuid().optional(),
    /** Selectors must belong to the observed elements of the step (technical §7.2 invariant 5). */
    anchor: anchorSpecSchema.optional(),
  })
  .strict();
export type UpdateGuidance = z.infer<typeof updateGuidanceDto>;

/** Mirror of the domain's closed list (functional §5.6, product decision). */
export const DISMISSAL_REASONS = [
  'not_relevant',
  'already_handled',
  'out_of_guidance_scope',
  'revisit_later',
  'other',
] as const;
export type DismissalReason = (typeof DISMISSAL_REASONS)[number];

export const dismissGuidanceDto = z
  .object({ reason: z.enum(DISMISSAL_REASONS), note: z.string().max(500).optional() })
  .strict();
export type DismissGuidance = z.infer<typeof dismissGuidanceDto>;

/**
 * What every decision on a guidance answers (technical §8.2: a command returns
 * identifier and version). `configurationVersion` is the release appended when
 * the decision changed what the SDK serves, null otherwise.
 */
export const guidanceDecisionResultDto = z.object({
  id: z.string().uuid(),
  state: z.enum(GUIDANCE_STATES),
  rowVersion: z.number().int().min(0),
  currentVersion: z.number().int().min(0),
  configurationVersion: z.number().int().min(1).nullable(),
});
export type GuidanceDecisionResult = z.infer<typeof guidanceDecisionResultDto>;

/** Actions journalised on a guidance (technical §23.6), oldest first in the history. */
export const GUIDANCE_HISTORY_ACTIONS = [
  'guidance.proposed',
  'guidance.updated',
  'guidance.approved',
  'guidance.reverted',
  'guidance.dismissed',
  'guidance.published',
  'guidance.disabled',
  'guidance.deleted',
] as const;
export type GuidanceHistoryAction = (typeof GUIDANCE_HISTORY_ACTIONS)[number];

export const guidanceHistoryEntryDto = z.object({
  action: z.enum(GUIDANCE_HISTORY_ACTIONS),
  /** Null for the engine's proposal. */
  actorId: z.string().uuid().nullable(),
  actorName: z.string().nullable(),
  occurredAt: z.string().datetime(),
  metadata: z.record(z.union([z.string(), z.number(), z.boolean()])).nullable(),
});
export type GuidanceHistoryEntry = z.infer<typeof guidanceHistoryEntryDto>;
